import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import { Form, Button, Table, Modal, Row, Col } from 'react-bootstrap';

interface PriceHistory {
    _id?: string;
    month: string;
    year: number;
    avgPrice: number;
}

interface Locality {
    _id: string;
    name: string;
    city: { _id: string, name: string } | string;
    avgPricePerSqFt: number;
    oldAvgPricePerSqFt: number;
    growthPercentage: number;
    growthScore: number;
    priceHistory: PriceHistory[];
}

const AdminPriceTrends = () => {
    const { token } = useAuth();
    const [localities, setLocalities] = useState<Locality[]>([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingLocality, setEditingLocality] = useState<Locality | null>(null);
    const [formData, setFormData] = useState({
        avgPricePerSqFt: 0,
        oldAvgPricePerSqFt: 0,
        growthPercentage: 0,
        growthScore: 0,
        priceHistory: [] as PriceHistory[]
    });

    const fetchLocalities = async () => {
        try {
            const res = await fetch('http://localhost:5000/api/locality-trends', {
                headers: { Authorization: `Bearer ${token}` }
            });
            const data = await res.json();
            setLocalities(data);
        } catch (error) {
            toast.error('Failed to fetch localities');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (token) fetchLocalities();
    }, [token]);

    const handleEdit = (locality: Locality) => {
        setEditingLocality(locality);
        setFormData({
            avgPricePerSqFt: locality.avgPricePerSqFt || 0,
            oldAvgPricePerSqFt: locality.oldAvgPricePerSqFt || 0,
            growthPercentage: locality.growthPercentage || 0,
            growthScore: locality.growthScore || 0,
            priceHistory: [...(locality.priceHistory || [])]
        });
        setShowModal(true);
    };

    const handleInputChange = (e: React.ChangeEvent<any>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: parseFloat(value) || 0 }));
    };

    const calculateGrowth = () => {
        const current = formData.avgPricePerSqFt;
        const old = formData.oldAvgPricePerSqFt;
        if (old === 0) return;
        
        const growth = ((current - old) / old) * 100;
        let score = 0.5;
        if (growth >= 20) score = 2;
        else if (growth >= 10) score = 1.5;
        else if (growth >= 5) score = 1;

        setFormData(prev => ({ 
            ...prev, 
            growthPercentage: parseFloat(growth.toFixed(2)),
            growthScore: score
        }));
    };

    const handleHistoryChange = (index: number, field: string, value: any) => {
        const newHistory = [...formData.priceHistory];
        newHistory[index] = { ...newHistory[index], [field]: field === 'avgPrice' || field === 'year' ? parseInt(value) || 0 : value };
        setFormData(prev => ({ ...prev, priceHistory: newHistory }));
    };

    const addHistoryRow = () => {
        console.log('Adding history row to top...');
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const newPoint = { 
            month: months[0], 
            year: new Date().getFullYear(), 
            avgPrice: formData.avgPricePerSqFt || 0 
        };
        
        setFormData(prev => ({
            ...prev,
            priceHistory: [newPoint, ...prev.priceHistory] // Prepend!
        }));
    };

    const removeHistoryRow = (index: number) => {
        setFormData(prev => ({
            ...prev,
            priceHistory: prev.priceHistory.filter((_, i) => i !== index)
        }));
    };

    const handleSync = async (localityId: string) => {
        try {
            const res = await fetch(`http://localhost:5000/api/locality-trends/sync/${localityId}`, {
                method: 'PUT',
                headers: { Authorization: `Bearer ${token}` }
            });
            const data = await res.json();
            if (res.ok) {
                toast.success('Synced with live project data!');
                fetchLocalities();
            } else {
                toast.error(data.message || 'Sync failed');
            }
        } catch (error) {
            toast.error('Sync error');
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            const res = await fetch(`http://localhost:5000/api/locality-trends/${editingLocality?._id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify(formData)
            });

            if (res.ok) {
                toast.success('Price trends updated successfully');
                setShowModal(false);
                fetchLocalities();
            } else {
                toast.error('Failed to update price trends');
            }
        } catch (error) {
            toast.error('Server error');
        }
    };

    return (
        <AdminLayout>
            <div className="flex justify-between items-center mb-8">
                <h3 className="text-3xl md:text-4xl font-bold text-black leading-none">Locality Price Trends</h3>
            </div>

            <div className="bg-white rounded-md shadow-sm border border-blue-900/10 overflow-hidden">
                <Table responsive hover className="mb-0">
                    <thead className="bg-white border-b border-gray-100">
                        <tr>
                            <th className="px-6 py-5 text-[12px] md:text-[13px] font-bold text-black">Locality</th>
                            <th className="px-6 py-5 text-[12px] md:text-[13px] font-bold text-black">Current Avg (₹)</th>
                            <th className="px-6 py-5 text-[12px] md:text-[13px] font-bold text-black">Old Avg (₹)</th>
                            <th className="px-6 py-5 text-[12px] md:text-[13px] font-bold text-black">Growth %</th>
                            <th className="px-6 py-5 text-[12px] md:text-[13px] font-bold text-black">Score</th>
                            <th className="px-6 py-5 text-[12px] md:text-[13px] font-bold text-black">History</th>
                            <th className="px-6 py-5 text-right text-[12px] md:text-[13px] font-bold text-black">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                        {localities.map((loc) => (
                            <tr key={loc._id}>
                                <td className="px-6 py-5 text-[15px] md:text-[16px] font-bold text-black underline underline-offset-4 decoration-black/10">{loc.name}</td>
                                <td className="px-6 py-5 font-bold text-black">₹{loc.avgPricePerSqFt?.toLocaleString()}</td>
                                <td className="px-6 py-5 font-bold text-black">₹{loc.oldAvgPricePerSqFt?.toLocaleString()}</td>
                                <td className="px-4 py-4">
                                    <span className={`px-2 py-1 rounded-md text-xs font-black ${loc.growthPercentage >= 10 ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                                        {loc.growthPercentage}%
                                    </span>
                                </td>
                                <td className="px-6 py-5">
                                    <span className="bg-white border-2 border-[#05144b] text-[#05144b] px-4 py-1 rounded-md text-[13px] font-bold shadow-sm">
                                        {loc.growthScore}
                                    </span>
                                </td>
                                <td className="px-6 py-5 text-black text-[13px] font-bold italic">{loc.priceHistory?.length || 0} Points</td>
                                <td className="px-4 py-4">
                                    <div className="flex gap-2">
                                        <Button variant="light" size="sm" onClick={() => handleEdit(loc)} title="Edit Manually" className="rounded-md border border-gray-100">
                                            <i className="fa-solid fa-pen-to-square text-black"></i>
                                        </Button>
                                        <Button variant="light" size="sm" onClick={() => handleSync(loc._id)} title="Sync with Live Projects" className="rounded-md border border-gray-100">
                                            <i className="fa-solid fa-rotate text-black"></i>
                                        </Button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </div>

            <Modal show={showModal} onHide={() => setShowModal(false)} size="xl" centered contentClassName="rounded-md border-0 shadow-2xl">
                <Form onSubmit={handleSubmit}>
                    <Modal.Header closeButton className="border-0 px-5 pt-5">
                        <Modal.Title className="font-bold text-black tracking-tight">Edit Price Trends: {editingLocality?.name}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="px-5 pb-5">
                        <Row className="mb-4">
                            <Col md={4}>
                                <Form.Group>
                                    <Form.Label className="text-[11px] font-bold uppercase text-black mb-2">Current Avg Price (₹/sqft)</Form.Label>
                                    <Form.Control type="number" name="avgPricePerSqFt" value={formData.avgPricePerSqFt} onChange={handleInputChange} className="rounded-md py-3 border-gray-100 font-bold text-black focus:ring-2 focus:ring-black" />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group>
                                    <Form.Label className="text-[11px] font-bold uppercase text-black mb-2">Price 1 Year Ago (₹/sqft)</Form.Label>
                                    <Form.Control type="number" name="oldAvgPricePerSqFt" value={formData.oldAvgPricePerSqFt} onChange={handleInputChange} className="rounded-md py-3 border-gray-100 font-bold text-black focus:ring-2 focus:ring-black" />
                                </Form.Group>
                            </Col>
                            <Col md={4} className="d-flex align-items-end">
                                <Button type="button" onClick={calculateGrowth} className="w-100 rounded-md py-3 font-bold bg-white border-2 border-[#05144b] text-[#05144b] hover:bg-blue-50 uppercase text-xs shadow-md">Calculate Growth & Score</Button>
                            </Col>
                        </Row>
                        <Row className="mb-4">
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="text-[11px] font-bold uppercase text-black mb-2">Growth Percentage (%)</Form.Label>
                                    <Form.Control type="number" step="0.01" name="growthPercentage" value={formData.growthPercentage} onChange={handleInputChange} className="rounded-md py-3 border-gray-100 font-bold bg-white text-black focus:ring-2 focus:ring-black" />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="text-[11px] font-bold uppercase text-black mb-2">Growth Score (0.5 to 2.0)</Form.Label>
                                    <Form.Control type="number" step="0.1" name="growthScore" value={formData.growthScore} onChange={handleInputChange} className="rounded-md py-3 border-gray-100 font-bold bg-white text-black focus:ring-2 focus:ring-black" />
                                </Form.Group>
                            </Col>
                        </Row>

                        <hr className="my-5 border-blue-900/10" />
                        
                        <div className="d-flex justify-content-between align-items-center mb-4">
                            <h5 className="font-bold text-black uppercase">Historical Data (Chart Points - {formData.priceHistory.length})</h5>
                            <Button type="button" size="sm" onClick={addHistoryRow} className="rounded-md bg-white border-2 border-[#05144b] text-[#05144b] hover:bg-blue-50 px-4 py-2 font-bold uppercase text-[10px] shadow-sm">Add Point</Button>
                        </div>

                        <div style={{ maxHeight: '400px', overflowY: 'auto' }} className="border border-blue-900/10 rounded-md p-4 bg-white">
                            <Table borderless size="sm">
                                <thead>
                                    <tr>
                                        <th className="text-[11px] font-bold uppercase text-black">Month</th>
                                        <th className="text-[11px] font-bold uppercase text-black">Year</th>
                                        <th className="text-[11px] font-bold uppercase text-black">Price (₹/sqft)</th>
                                        <th className="text-[11px] font-bold uppercase text-black">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {formData.priceHistory.map((point, idx) => (
                                        <tr key={idx}>
                                            <td>
                                                <Form.Select value={point.month} onChange={(e) => handleHistoryChange(idx, 'month', e.target.value)} className="rounded-md border-blue-900/10 text-xs font-bold text-blue-950">
                                                    {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map(m => <option key={m} value={m}>{m}</option>)}
                                                </Form.Select>
                                            </td>
                                            <td>
                                                <Form.Control type="number" value={point.year} onChange={(e) => handleHistoryChange(idx, 'year', e.target.value)} className="rounded-md border-blue-900/10 text-xs font-bold text-blue-950" />
                                            </td>
                                            <td>
                                                <Form.Control type="number" value={point.avgPrice} onChange={(e) => handleHistoryChange(idx, 'avgPrice', e.target.value)} className="rounded-md border-blue-900/10 text-xs font-bold text-blue-950" />
                                            </td>
                                            <td>
                                                <Button type="button" variant="link" className="text-danger p-0 mt-1" onClick={() => removeHistoryRow(idx)}><i className="fa-solid fa-trash"></i></Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    </Modal.Body>
                    <Modal.Footer className="border-0 px-5 pb-5">
                        <Button variant="light" onClick={() => setShowModal(false)} className="rounded-md px-5 py-3 font-bold uppercase text-xs border border-gray-100 text-black">Cancel</Button>
                        <Button type="submit" className="rounded-md bg-white border-2 border-[#05144b] text-[#05144b] hover:bg-blue-50 px-8 py-3 font-bold uppercase text-xs shadow-md">Save Trend Data</Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </AdminLayout>
    );
};

export default AdminPriceTrends;


